import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button, Tooltip } from "antd";
import { getChangeInControls } from "../redux/actions/CompanyAction";
import {
  emdash,
  RadioSelector,
  toDollar,
} from "../utils/Constants/systemSetting";
import { exportToCSV } from "../utils/ExecutiveData/csvExport";
import CohortSelector from "./ExecutiveOverview/CohortSection/CohortSelector";
import {
  getExecPosition,
  getExecPositionGroup,
  getExecPositionScore,
  posGroupMatch,
} from "../utils/ExecutiveData/getExecPosition.mjs";
import getCIKtickerMap from "../utils/Constants/getCIKtickerMap";
import { CitationLink } from "./CitationLink";

export default function ChangeInControls({
  companyInfo,
  reportYear,
  PDFprocessing,
  setReportFiscalYear,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.UserReducer);
  const { subscription } = useSelector((state) => state.CustomerReducer);
  const isTier2 = useMemo(() => {
    if (user?.tier2access || subscription?.plan?.metadata?.isTier2 === "true") {
      return true;
    }
    return false;
  }, [user, subscription]);

  const { csuite: allExecutives } = useSelector(
    (state) => state.ExecutivesReducer,
  );

  const { companies } = useSelector((state) => state.CompaniesReducer);

  const [companyExecs, setCompanyExecs] = useState([]);

  const [cohortTableTickers, setCohortTableTickers] = useState([]);
  const [cohortSelected, setCohortSelected] = useState("None");

  function addSpaceBetweenCapitalizedWords(str) {
    return str.replace(/([a-z])([A-Z])/g, "$1 $2");
  }
  function toTitleCase(str) {
    return str.replace(
      /\w\S*/g,
      (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
    );
  }

  useEffect(() => {
    const executiveData = [];
    for (const executive of allExecutives) {
      if (executive.Ticker === companyInfo.Ticker) {
        executiveData.push(executive);
      }
      if (cohortTableTickers && cohortTableTickers.length) {
        if (cohortTableTickers.includes(executive.Ticker)) {
          executiveData.push(executive);
        }
      }
    }

    setCompanyExecs(executiveData);
  }, [companyInfo.Ticker, allExecutives, cohortTableTickers]);

  const formatDataForCSVexport = useCallback((columns, data) => {
    const compensationDecisionOutputData = [];
    if (data?.length) {
      compensationDecisionOutputData.push(
        ...data?.map((item) => {
          return { ...item };
        }),
      );
    }
    const compensationDecisionHeaders = [];
    columns.forEach((item) => {
      if (!compensationDecisionHeaders.includes(item.dataIndex)) {
        compensationDecisionHeaders.push(item.dataIndex);
      }
    });
    const formattedBoardPayData = compensationDecisionOutputData.map((item) => {
      const formattedItem = {};
      columns.forEach((keyDetails, index) => {
        if (keyDetails?.dataIndex) {
          formattedItem[`"${keyDetails.title}"`] =
            item?.[keyDetails.dataIndex] &&
            (item?.[keyDetails?.dataIndex].length > 2 ||
              item?.[keyDetails?.dataIndex] >= 0)
              ? item[keyDetails?.dataIndex].toString()?.replaceAll(",", "")
              : "";
        }
      });
      return formattedItem;
    });
    return formattedBoardPayData;
  }, []);

  const formatNestedDataForCSVexport = useCallback((columns, data) => {
    const compensationDecisionOutputData = [];
    if (data?.length) {
      compensationDecisionOutputData.push(
        ...data?.map((item) => {
          return {
            ...item,
          };
        }),
      );
    }

    const formattedBoardPayData = compensationDecisionOutputData.map((item) => {
      const formattedItem = {};
      columns.forEach((keyDetails, index) => {
        if (item[keyDetails?.dataIndex]) {
          formattedItem[
            `"${
              keyDetails?.dataIndex === "category"
                ? "Category"
                : keyDetails?.dataIndex === "value"
                  ? "Details"
                  : keyDetails?.dataIndex
            }"`
          ] =
            item?.[keyDetails.dataIndex] &&
            (item?.[keyDetails?.dataIndex].length > 2 ||
              item?.[keyDetails?.dataIndex] >= 0 ||
              item[keyDetails?.dataIndex]?.props?.title ||
              item[keyDetails?.dataIndex]?.props?.children?.props?.children)
              ? keyDetails.dataIndex === "category"
                ? toTitleCase(
                    addSpaceBetweenCapitalizedWords(
                      item[keyDetails?.dataIndex]
                        .toString()
                        ?.replaceAll(",", ""),
                    ),
                  )
                : item[keyDetails?.dataIndex]?.props?.children?.props
                      ?.children > 0
                  ? `"${
                      item[keyDetails?.dataIndex]?.props?.children?.props
                        ?.children || ""
                    }"`
                  : `"${item[keyDetails?.dataIndex]?.props?.title || ""}"` || ""
              : "-";
        }
      });
      return formattedItem;
    });
    return formattedBoardPayData;
  }, []);

  const [salaryMultipleToggle, setSalaryMultipleToggle] =
    useState("Dollar Amount");

  const remapPayments = useCallback(
    (exec, ticker) => {
      const newPayments = {};

      const payments = exec?.payments;
      if (!payments || typeof payments !== "object") return newPayments;
      for (const [key, val] of Object.entries(payments)) {
        let formattedVal = null;
        if (salaryMultipleToggle !== "Dollar Amount" && val) {
          formattedVal = "N/A";

          //TODO: Match based on company ticker & position title next

          const execMatch = companyExecs.find(
            (item) => item.name === exec.name && item.Ticker === exec.Ticker,
          );

          let backupExecFound = null;

          const reportYearFormatted =
            typeof reportYear === "string" && reportYear.includes("FY ")
              ? reportYear.replace("FY ", "")
              : null;

          if (!execMatch && exec) {
            const execPosGroups = [getExecPositionGroup(exec.position)];
            const chiefPositions = [];
            let bestScore = Infinity; //lowest is best
            let bestPos = "";
            for (const execPosGroup of execPosGroups) {
              if (execPosGroup === "N/A") continue;
              let currentScore = getExecPositionScore(execPosGroup);
              if (execPosGroup.formatted.includes("Chief ")) {
                chiefPositions.push(execPosGroup);
              }
              if (currentScore < bestScore) {
                bestScore = currentScore;
                bestPos = execPosGroup;
              }
            }
            const posMatch = chiefPositions.length
              ? chiefPositions
              : bestPos
                ? [bestPos]
                : ["N/A"];

            if (!posMatch.includes("N/A")) {
              const backupExecMatch = companyExecs.find(
                (SCTexec) =>
                  reportYearFormatted &&
                  SCTexec.Ticker === exec.Ticker &&
                  posGroupMatch(reportYearFormatted, SCTexec, posMatch),
              );

              backupExecFound = backupExecMatch;
            }
          }

          const execFound = execMatch || backupExecFound;
          if (
            execFound &&
            typeof reportYear === "string" &&
            reportYear.includes("FY ")
          ) {
            const salary =
              execFound?.compensation?.[reportYear.replace("FY ", "")]?.salary;
            if (salary) {
              formattedVal = val / salary;
            }
          }
        } else {
          formattedVal = val;
        }
        newPayments[addSpaceBetweenCapitalizedWords(key)] = formattedVal;
      }
      return newPayments;
    },
    [companyExecs, reportYear, salaryMultipleToggle],
  );

  const { changeInControls } = useSelector((state) => state.CompanyReducer);

  // const [sortOrder, setSortOrder] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("ACCESS_TOKEN");
    const ticker = companyInfo?.Ticker;
    const CIK = companyInfo?.CIK;

    if (ticker && !cohortTableTickers.length) {
      dispatch(
        getChangeInControls(
          JSON.stringify([{ ticker, CIK }]),
          reportYear,
          token,
        ),
      );
    } else if (ticker && cohortTableTickers.length) {
      const CIKtickerMap = getCIKtickerMap(
        [ticker, ...cohortTableTickers.filter((item) => item !== ticker)],
        companies,
      );

      dispatch(getChangeInControls(CIKtickerMap, reportYear, token));
    }
  }, [companies, companyInfo, reportYear, cohortTableTickers, dispatch]);

  const getNEOspecificCICcolumns = useCallback(
    (isPeerGroupTable = false) => {
      const perkOrder = {
        Severance: 1,
        Bonus: 2,
        "Equity Awards": 3,
        "Short Term Incentive Awards": 4,
        "Long Term Incentive Awards": 5,
        RSUs: 6,
        PSUs: 7,
        "Health Benefits": 8,
        Other: 9,
        Total: 10,
      };
      const tickersNeeded = isPeerGroupTable
        ? [companyInfo?.Ticker, ...cohortTableTickers]
        : [companyInfo?.Ticker];

      const executiveSpecificBenefits = tickersNeeded
        .map((ticker) => {
          const execBenefitsArray =
            changeInControls?.[ticker]?.ChangeInControlDetails
              ?.NEO_Specific_Benefits;

          if (!execBenefitsArray) return [];
          const execBenefitsArrayWithTicker = execBenefitsArray.map((item) => ({
            ...item,
            Ticker: ticker,
          }));
          return execBenefitsArrayWithTicker;
        })
        .flat(1);
      // changeInControls?.[companyInfo?.Ticker]?.ChangeInControlDetails
      //   ?.NEO_Specific_Benefits;

      if (!executiveSpecificBenefits) return [];

      const defaultColumns = [
        {
          title: "Executive Name",
          dataIndex: "name",
          key: "name",
          align: "center",
          sorter: (a, b) => {
            const aVal = a?.name ?? "";
            const bVal = b?.name ?? "";

            return aVal.localeCompare(bVal);
          },
        },
        {
          title: "Position",
          dataIndex: "position",
          key: "position",
          align: "center",
          sorter: (a, b) => {
            const aVal = a?.position ?? "";
            const bVal = b?.position ?? "";

            return aVal.localeCompare(bVal);
          },
        },
      ];

      const otherExecutivePerks = [];

      executiveSpecificBenefits.forEach((exec) => {
        if (!exec?.payments) return;
        Object.entries(exec.payments).forEach(([perkName, value]) => {
          if (!perkName) return;
          if (
            !executiveSpecificBenefits.some((exec) => {
              return exec?.payments?.[perkName] > 0;
            })
          ) {
            return;
          }
          if (
            !otherExecutivePerks.includes(
              addSpaceBetweenCapitalizedWords(perkName),
            ) &&
            addSpaceBetweenCapitalizedWords(perkName) !== "Total"
          ) {
            otherExecutivePerks.push(addSpaceBetweenCapitalizedWords(perkName));
          }
        });
      });

      const otherExecutivePerkColumns = otherExecutivePerks
        .sort((a, b) => (perkOrder?.[a] || 0) - (perkOrder?.[b] || 0))
        .map((perkName, index) => ({
          title: addSpaceBetweenCapitalizedWords(perkName),
          dataIndex: addSpaceBetweenCapitalizedWords(perkName),
          key: addSpaceBetweenCapitalizedWords(perkName),
          align: "center",
          sorter: (a, b, sortOrder) => {
            const aPayments = remapPayments(a);
            const bPayments = remapPayments(b);
            const aVal = aPayments?.[addSpaceBetweenCapitalizedWords(perkName)];
            const bVal = bPayments?.[addSpaceBetweenCapitalizedWords(perkName)];

            const isBlank = (v) =>
              v === null ||
              v === undefined ||
              v === "None" ||
              v === "N/A" ||
              v === 0 ||
              v === "";

            const aMissing = isBlank(aVal);
            const bMissing = isBlank(bVal);

            if (aMissing && bMissing) return 0;

            if (aMissing) return sortOrder === "ascend" ? Infinity : -Infinity;
            if (bMissing) return sortOrder === "ascend" ? -Infinity : Infinity;

            return aVal - bVal;
          },
          render: (item) =>
            item
              ? salaryMultipleToggle === "Dollar Amount"
                ? toDollar(item)
                : typeof item === "number"
                  ? item.toFixed(1)
                  : item
              : emdash,
        }));

      const allExecutivePerkColumns = [
        ...defaultColumns,
        ...otherExecutivePerkColumns,
        {
          title: "Total",
          dataIndex: "Total",
          key: "Total",
          align: "center",
          sorter: (a, b, sortOrder) => {
            const aVal = a.payments?.["Total"];
            const bVal = b.payments?.["Total"];

            const isBlank = (v) => v === null || v === undefined || v === "";

            const aMissing = isBlank(aVal);
            const bMissing = isBlank(bVal);

            if (aMissing && bMissing) return 0;

            if (aMissing) return sortOrder === "ascend" ? Infinity : -Infinity;
            if (bMissing) return sortOrder === "ascend" ? -Infinity : Infinity;

            return aVal - bVal;
          },
          render: (item) =>
            item
              ? salaryMultipleToggle === "Dollar Amount"
                ? toDollar(item)
                : typeof item === "number"
                  ? item.toFixed(1)
                  : item
              : emdash,
        },
      ];
      if (isPeerGroupTable) {
        allExecutivePerkColumns.unshift({
          title: "Ticker",
          dataIndex: "Ticker",
          key: "Ticker",
          align: "center",
          sorter: (a, b) => {
            const aVal = a?.position ?? "";
            const bVal = b?.position ?? "";

            return aVal.localeCompare(bVal);
          },
        });
        allExecutivePerkColumns.push({
          title: "Citation",
          dataIndex: "Citation",
          key: "Citation",
          align: "center",
          render: (citation, col) => (
            <CitationLink
              citation={citation}
              ticker={col?.Ticker}
              reportYear={reportYear}
            />
          ),
        });
      }

      return allExecutivePerkColumns;
    },
    [
      companyInfo,
      changeInControls,
      cohortTableTickers,
      salaryMultipleToggle,
      remapPayments,
    ],
  );

  const getNEOspecificCICdata = useCallback(
    (isPeerGroupTable = false) => {
      const tickersNeeded = isPeerGroupTable
        ? [companyInfo?.Ticker, ...cohortTableTickers]
        : [companyInfo?.Ticker];

      const allNEOSpecificCICData = [];
      for (const ticker of tickersNeeded) {
        const executiveSpecificBenefits =
          changeInControls?.[ticker]?.ChangeInControlDetails
            ?.NEO_Specific_Benefits;

        if (!executiveSpecificBenefits) continue;

        const executiveSpecificBenefitsWithTicker =
          executiveSpecificBenefits.map((item) => ({
            ...item,
            Ticker: ticker,
          }));

        const allCompanyExecBenefits = executiveSpecificBenefitsWithTicker.map(
          (exec) => {
            const payments = remapPayments(exec);
            return {
              Ticker: ticker,
              ...exec,
              ...payments,
              Citation: exec?.source || "",
            };
          },
        );
        allNEOSpecificCICData.push(...allCompanyExecBenefits);
      }
      return allNEOSpecificCICData;
    },
    [changeInControls, cohortTableTickers, companyInfo, remapPayments],
  );

  const companyTriggers =
    changeInControls?.[companyInfo?.Ticker]?.ChangeInControlDetails?.Triggers;
  const companyBenefits =
    changeInControls?.[companyInfo?.Ticker]?.ChangeInControlDetails?.Benefits;
  const companyGovernance =
    changeInControls?.[companyInfo?.Ticker]?.ChangeInControlDetails?.Governance;

  const getGovernanceData = useCallback(
    (isPeerGroupTable = false) => {
      const tickersNeeded = isPeerGroupTable
        ? [companyInfo?.Ticker, ...cohortTableTickers]
        : [companyInfo?.Ticker];

      const allGovernanceData = [];
      for (const ticker of tickersNeeded) {
        const tickerGovernance =
          changeInControls?.[ticker]?.ChangeInControlDetails?.Governance;
        if (!tickerGovernance) continue;

        Object.keys(
          changeInControls?.[ticker]?.ChangeInControlDetails?.Governance,
        )
          .filter((key) => isPeerGroupTable || key !== "restrictiveCovenants")
          .sort((a, b) => {
            if (a === "restrictiveCovenants") return 1;
            if (b === "restrictiveCovenants") return -1;
            return null;
            // return a.localeCompare(b);
          })
          .forEach((key, index) => {
            const checkMark =
              tickerGovernance?.[key] &&
              tickerGovernance[key] !== "None" &&
              tickerGovernance[key] !== "N/A" &&
              !(
                typeof tickerGovernance[key] === "string" &&
                (tickerGovernance[key].startsWith("None ") ||
                  tickerGovernance[key].startsWith("No "))
              ) &&
              (key !== "restrictiveCovenants" ||
                (tickerGovernance?.[key] && tickerGovernance?.[key].length)) ? (
                <Tooltip
                  title={
                    key === "restrictiveCovenants" &&
                    tickerGovernance[key].length
                      ? tickerGovernance[key].join(", ")
                      : tickerGovernance[key]
                  }
                >
                  <span style={{ fontSize: "15px" }}>✓</span>
                </Tooltip>
              ) : (
                ""
              );
            const curVal = {
              key: index,
              category: key,
              title: key,
              value: checkMark || " ",
            };
            curVal[ticker] = checkMark || " "; //Leave space for excel exports
            if (allGovernanceData.some((item) => item.category === key)) {
              allGovernanceData[
                allGovernanceData.indexOf(
                  allGovernanceData.find((item) => item.category === key),
                )
              ][ticker] = checkMark || " "; //Leave space for excel exports
            } else {
              allGovernanceData.push(curVal);
            }
            return curVal;
          });
      }
      return allGovernanceData;
    },

    [companyInfo, changeInControls, cohortTableTickers],
  );

  const getGovernanceColumns = (isPeerGroupTable = false) => {
    const governanceColumns = [
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        render: (text) =>
          !text
            ? ""
            : toTitleCase(
                addSpaceBetweenCapitalizedWords(
                  text.replace(/([A-Z])/g, " $1").trim(),
                ),
              ),
      },
    ];
    if (!isPeerGroupTable) {
      governanceColumns.push({
        title: "Exists?",
        dataIndex: "value",
        key: "value",
      });
    }
    if (isPeerGroupTable) {
      for (const ticker of [companyInfo?.Ticker, ...cohortTableTickers]) {
        if (changeInControls?.[ticker]?.ChangeInControlDetails?.Governance) {
          governanceColumns.push({
            title: ticker,
            dataIndex: ticker,
            key: ticker,
          });
        }
      }
    }
    return governanceColumns;
  };

  const GovernanceTable = () => {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Table
          style={{ width: "500px" }}
          columns={getGovernanceColumns()}
          dataSource={getGovernanceData()}
          pagination={false}
        />
      </div>
    );
  };

  const getBenefitsData = useCallback(
    (isPeerGroupTable = false) => {
      const tickersNeeded = isPeerGroupTable
        ? [companyInfo?.Ticker, ...cohortTableTickers]
        : [companyInfo?.Ticker];

      const allGovernanceData = [];
      for (const ticker of tickersNeeded) {
        const tickerGovernance =
          changeInControls?.[ticker]?.ChangeInControlDetails?.Benefits;
        if (!tickerGovernance) continue;

        const showValues = [
          "healthcareContinuationMonths",
          "cashSeveranceMultiplier",
        ];

        const hideValues = [
          // "restrictiveCovenants",
          "cashSeveranceMultiplierDescription",
          "otherBenefitsValue",
        ];
        if (!changeInControls?.[ticker]?.ChangeInControlDetails) continue;
        Object.keys(
          changeInControls?.[ticker]?.ChangeInControlDetails?.Benefits,
        )
          .filter((key) => !hideValues.includes(key))
          .forEach((key, index) => {
            const checkMark =
              tickerGovernance[key] &&
              tickerGovernance[key] !== "None" &&
              tickerGovernance[key] !== "N/A" &&
              !(
                typeof tickerGovernance[key] === "string" &&
                (tickerGovernance[key].startsWith("None ") ||
                  tickerGovernance[key].startsWith("No "))
              ) ? (
                <Tooltip
                  title={
                    key === "cashSeveranceMultiplier"
                      ? tickerGovernance["cashSeveranceMultiplierDescription"]
                      : showValues.includes(key)
                        ? null
                        : tickerGovernance[key]
                  }
                >
                  {showValues.includes(key) ? (
                    <span style={{ fontSize: "13px" }}>
                      {tickerGovernance[key]}
                    </span>
                  ) : (
                    <span style={{ fontSize: "15px" }}>✓</span>
                  )}
                </Tooltip>
              ) : (
                ""
              );
            const curVal = {
              key: index,
              category: key,
              value: checkMark,
            };
            curVal[ticker] = checkMark || " "; //Leave space for excel exports
            if (allGovernanceData.some((item) => item.category === key)) {
              allGovernanceData[
                allGovernanceData.indexOf(
                  allGovernanceData.find((item) => item.category === key),
                )
              ][ticker] = checkMark || " "; //Leave space for excel exports
            } else {
              allGovernanceData.push(curVal);
            }
            return curVal;
          });
      }
      return allGovernanceData;
    },

    [companyInfo, changeInControls, cohortTableTickers],
  );

  const getBenefitsColumns = (isPeerGroupTable = false) => {
    const benefitsColumns = [
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        render: (text) =>
          !text
            ? ""
            : toTitleCase(
                addSpaceBetweenCapitalizedWords(
                  text
                    .replace("otherBenefitsDescription", "otherBenefits")
                    .replace(/([A-Z])/g, " $1")
                    .trim(),
                ),
              ),
      },
    ];
    if (!isPeerGroupTable) {
      benefitsColumns.push({
        title: "Exists?",
        dataIndex: "value",
        key: "value",
      });
    }
    if (isPeerGroupTable) {
      for (const ticker of [companyInfo?.Ticker, ...cohortTableTickers]) {
        if (changeInControls?.[ticker]?.ChangeInControlDetails?.Benefits) {
          benefitsColumns.push({
            title: ticker,
            dataIndex: ticker,
            key: ticker,
          });
        }
      }
    }
    return benefitsColumns;
  };

  const PeerChangeInControlBenefits = () => {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Table
          columns={getBenefitsColumns(true)}
          dataSource={getBenefitsData(true)}
          pagination={false}
        />
      </div>
    );
  };

  const GovernancePeerTable = () => {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Table
          // style={{ width: "500px" }}
          columns={getGovernanceColumns(true)}
          dataSource={getGovernanceData(true)}
          pagination={false}
        />
      </div>
    );
  };

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (
      isInitialLoad &&
      changeInControls?.[companyInfo?.Ticker] &&
      !changeInControls?.[companyInfo?.Ticker]?.ChangeInControlDetails
        ?.AdditionalDetails
    ) {
      setReportFiscalYear("FY 2024");
      setIsInitialLoad(false);
    }
  }, [changeInControls, companyInfo, setReportFiscalYear, isInitialLoad]);

  const normalize = (text) => {
    function capitalize(str) {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
    return capitalize(
      text
        .toLowerCase()
        .replace(/\s/, " ")
        .replace(" of ", " in ")
        .replace("cash ", ""),
    );
  };

  const [showFullCICdetails, setShowFulllCICdetails] = useState(false);

  const PeerChangeInControlTriggers = useCallback(() => {
    const CICtriggerSummaryStats = {};
    const CICdoubleTriggerSummaryStats = [];

    return (
      <div style={{ marginLeft: 20 }}>
        {cohortTableTickers.map((ticker) => {
          const companyTiggers =
            changeInControls?.[ticker]?.ChangeInControlDetails?.Triggers;
          const CICtriggers = companyTiggers?.CIC_Triggers;

          if (CICtriggers) {
            CICtriggers.forEach((trigger) => {
              if (CICtriggerSummaryStats?.[normalize(trigger)]) {
                CICtriggerSummaryStats?.[normalize(trigger)].push(ticker);
              } else {
                CICtriggerSummaryStats[normalize(trigger)] = [ticker];
              }
            });
          }

          const doubleTrigger = companyTiggers?.DoubleTrigger;

          if (doubleTrigger) {
            CICdoubleTriggerSummaryStats.push(ticker);
          }

          if (!companyTiggers) return null;

          return (
            <>
              {showFullCICdetails && (
                <>
                  <p
                    style={{ marginTop: 15, fontSize: 20, fontWeight: "bold" }}
                  >
                    {ticker} Change in Control Triggers:
                  </p>
                  <ul style={{ marginBottom: 5 }}>
                    {CICtriggers ? (
                      CICtriggers.map((trigger) => {
                        return <li>{trigger}</li>;
                      })
                    ) : (
                      <p>N/A</p>
                    )}
                  </ul>
                  <div style={{ marginLeft: 17 }}>
                    <b>Double Trigger:</b> {doubleTrigger ? "Yes" : "No"}
                  </div>
                </>
              )}
            </>
          );
        })}
        {!showFullCICdetails && (
          <div>
            <h3>CIC Triggers Summary Stats</h3>
            {Object.entries(CICtriggerSummaryStats)
              .sort(([a, aTickers], [b, bTickers]) => {
                return bTickers.length - aTickers.length;
              })
              .map(([trigger, tickers]) => {
                return (
                  <p>
                    {trigger}:{" "}
                    <span style={{ pointer: "help" }}>
                      <Tooltip title={tickers.join(", ")}>
                        <b style={{ pointer: "help" }}>{tickers.length}</b>
                      </Tooltip>
                    </span>
                  </p>
                );
              })}
            <div style={{ marginTop: 10 }}>
              <b style={{ fontSize: 16 }}>
                Double Triggers:{" "}
                <span style={{ pointer: "help" }}>
                  <Tooltip title={CICdoubleTriggerSummaryStats.join(", ")}>
                    <b style={{ pointer: "help" }}>
                      {CICdoubleTriggerSummaryStats.length} /{" "}
                      {cohortTableTickers.length} Peers
                    </b>
                  </Tooltip>
                </span>
              </b>
            </div>
          </div>
        )}
      </div>
    );
  }, [changeInControls, cohortTableTickers, showFullCICdetails]);

  return (
    <>
      {!isTier2 ? (
        <div
          className="comp-ai-section col-lg-9 executives "
          style={{ height: "100%", padding: 50 }}
        >
          <h3
            style={{
              textAlign: "center",
              fontWeight: "normal",
              fontSize: 22,
            }}
          >
            <p style={{ fontWeight: "bold" }}>
              This page is for C-Suite Comp&reg; Enterprise users only.
            </p>
            <br />
            <p>
              Contact your account manager to access Severance / Change in
              Control Data
            </p>
          </h3>
        </div>
      ) : (
        <>
          <div className="company-calculator-section col-lg-9 executives p-0">
            {!changeInControls?.[companyInfo?.Ticker]?.ChangeInControlDetails
              ?.AdditionalDetails ? (
              <h2 style={{ textAlign: "center", marginTop: 50 }}>
                Severance / Change in Control Data for {companyInfo?.Ticker}{" "}
                Coming Soon for {reportYear}
              </h2>
            ) : (
              <div>
                <h2>
                  Executive-Specific Severance / Change in Control Benefits
                </h2>
                <RadioSelector
                  options={["Dollar Amount", "Salary Multiplier"]}
                  state={salaryMultipleToggle}
                  setState={setSalaryMultipleToggle}
                  heading={"Executive-Specific Data View:"}
                  style={{ marginBottom: 10 }}
                />
                {!PDFprocessing && (
                  <div style={{ width: "100%" }}>
                    <Button
                      style={{ marginBottom: 10 }}
                      onClick={() =>
                        exportToCSV(
                          formatDataForCSVexport(
                            getNEOspecificCICcolumns(),
                            getNEOspecificCICdata(),
                          ),
                          `${companyInfo?.Ticker} 
                           Severance / Change in Control ${
                             new Date().toString().split("GMT-")[0]
                           }.csv`,
                        )
                      }
                    >
                      Export Severance / Change in Control as CSV
                    </Button>
                  </div>
                )}
                <Table
                  columns={getNEOspecificCICcolumns()}
                  dataSource={getNEOspecificCICdata()}
                  pagination={{ hideOnSinglePage: true, pageSize: 25 }}
                />
                {changeInControls?.[companyInfo?.Ticker]?.ChangeInControlDetails
                  ?.NEO_Specific_Benefits?.[0]?.source && (
                  <p style={{ marginTop: 10 }}>
                    <b>Citation: </b>
                    <CitationLink
                      citation={
                        changeInControls?.[companyInfo?.Ticker]
                          ?.ChangeInControlDetails?.NEO_Specific_Benefits?.[0]
                          ?.source
                      }
                      ticker={companyInfo?.Ticker}
                      reportYear={reportYear}
                    />
                  </p>
                )}
                {changeInControls?.[companyInfo?.Ticker]?.ChangeInControlDetails
                  ?.AdditionalDetails && (
                  <p style={{ marginTop: 10 }}>
                    <b>Note: </b>

                    {
                      changeInControls?.[companyInfo?.Ticker]
                        ?.ChangeInControlDetails?.AdditionalDetails
                    }
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="company-calculator-section col-lg-12 executives mt-5 p-0">
            <div>
              <h2>Company-Wide Severance / Change in Control Details</h2>
              {/* <p style={{ textAlign: "center" }}>
                Company-Wide Severance / Change in Control Details coming soon
              </p> */}
              <div style={{ margin: 20 }} />
              <h3>Change in Control Triggers</h3>
              <div style={{ marginLeft: 20 }}>
                <ul>
                  {companyTriggers?.CIC_Triggers ? (
                    companyTriggers?.CIC_Triggers.map((trigger) => (
                      <li>{trigger}</li>
                    ))
                  ) : (
                    <p>N/A</p>
                  )}
                </ul>
                {companyTriggers?.DoubleTrigger ? (
                  <div>
                    <b>Double Trigger:</b>{" "}
                    {companyTriggers?.DoubleTrigger ? "Yes" : "No"}
                  </div>
                ) : null}
              </div>
              <div style={{ margin: 20 }} />

              <h3>Change in Control Benefits</h3>
              <div style={{ marginLeft: 20 }}>
                <ul>
                  {companyBenefits ? (
                    Object.entries(companyBenefits).map(
                      ([benefitKey, benefitValue]) => (
                        <div>
                          <b>
                            {toTitleCase(
                              addSpaceBetweenCapitalizedWords(benefitKey),
                            )}
                            :{" "}
                          </b>
                          {benefitValue || "N/A"}
                        </div>
                      ),
                    )
                  ) : (
                    <p>N/A</p>
                  )}
                </ul>
              </div>
              <div style={{ margin: 20 }} />

              <h3>Change in Control Governance</h3>
              <div style={{ marginLeft: 20 }}>
                {!PDFprocessing && (
                  <div style={{ width: "100%" }}>
                    <Button
                      style={{ marginBottom: 10 }}
                      onClick={() =>
                        exportToCSV(
                          formatNestedDataForCSVexport(
                            getGovernanceColumns(),
                            getGovernanceData(),
                          ),
                          `${companyInfo?.Ticker} 
                           Severance / Change in Control ${
                             new Date().toString().split("GMT-")[0]
                           }.csv`,
                        )
                      }
                    >
                      Export Change in Control Governance as CSV
                    </Button>
                  </div>
                )}
                <GovernanceTable />
                <div style={{ marginLeft: 50 }}>
                  <b>Restrictive Covenants:</b>
                  {companyGovernance?.restrictiveCovenants &&
                  companyGovernance.restrictiveCovenants.length ? (
                    <div>
                      <ul>
                        {companyGovernance?.restrictiveCovenants.map(
                          (trigger) => (
                            <li>{trigger}</li>
                          ),
                        )}
                      </ul>
                    </div>
                  ) : (
                    " None"
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="company-calculator-section col-lg-12 executives mt-5 p-0">
            <div className="company-calculator-section executives">
              <div style={{ margin: 10 }} />
              <hr />
              <h2 style={{ textAlign: "center" }}>Peer Group Comparison</h2>
              <p style={{ textAlign: "center" }}>
                Select a Comparison Group to compare to {companyInfo.Company}
                's Severance / Change in COntrol Benefits:
              </p>
              <CohortSelector
                heading={"Select Comparison Group:"}
                companyInfo={companyInfo}
                setCohortTableTickers={setCohortTableTickers}
                cohortTableSelector={cohortSelected}
                setCohortTableSelector={setCohortSelected}
                removeCustomCohort={true}
                additionalOptions={["None"]}
              />
              {cohortTableTickers.length ? (
                <div style={{ marginTop: 20 }}>
                  <h2>
                    Executive-Specific Severance / Change in Control
                    Benefits{" "}
                  </h2>
                  <RadioSelector
                    options={["Dollar Amount", "Salary Multiplier"]}
                    state={salaryMultipleToggle}
                    setState={setSalaryMultipleToggle}
                    heading={"Executive-Specific Data View:"}
                    style={{ marginBottom: 10 }}
                  />
                  {!PDFprocessing && (
                    <div style={{ width: "100%" }}>
                      <Button
                        style={{ marginBottom: 10 }}
                        onClick={() =>
                          exportToCSV(
                            formatDataForCSVexport(
                              getNEOspecificCICcolumns(true),
                              getNEOspecificCICdata(true),
                            ),
                            `${companyInfo?.Ticker} 
                           Severance / Change in Control ${
                             new Date().toString().split("GMT-")[0]
                           }.csv`,
                          )
                        }
                      >
                        Export Severance / Change in Control as CSV
                      </Button>
                    </div>
                  )}
                  <Table
                    columns={getNEOspecificCICcolumns(true)}
                    dataSource={getNEOspecificCICdata(true)}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 25 }}
                    size="small"
                    // expandable={{
                    //   rowExpandable: (record) => getRowExpandable(record),
                    //   expandedRowRender: (record) =>
                    //     getExpandedRowRender(record),
                    // }}
                  />
                  <div style={{ margin: 20 }} />
                  <h2>Company-Wide Severance / Change in Control Details</h2>
                  <h3>Change in Control Triggers </h3>
                  <Button
                    style={{
                      marginLeft: 20,
                      marginBottom: showFullCICdetails ? 0 : 10,
                    }}
                    onClick={() => setShowFulllCICdetails(!showFullCICdetails)}
                  >
                    {!showFullCICdetails ? "Show" : "Hide"} Full Peer Group
                    Triggers Details
                  </Button>
                  <PeerChangeInControlTriggers />

                  <div style={{ margin: 20 }} />

                  <h3>Change in Control Benefits </h3>
                  {!PDFprocessing && (
                    <div style={{ width: "100%" }}>
                      <Button
                        style={{ marginBottom: 10 }}
                        onClick={() =>
                          exportToCSV(
                            formatNestedDataForCSVexport(
                              getBenefitsColumns(true),
                              getBenefitsData(true),
                            ),
                            `${companyInfo?.Ticker} 
                           Severance / Change in Control ${
                             new Date().toString().split("GMT-")[0]
                           }.csv`,
                          )
                        }
                      >
                        Export Change in Control Benefits as CSV
                      </Button>
                    </div>
                  )}
                  <PeerChangeInControlBenefits />

                  <div style={{ margin: 20 }} />

                  <h3>Change in Control Governance</h3>
                  {!PDFprocessing && (
                    <div style={{ width: "100%" }}>
                      <Button
                        style={{ marginBottom: 10 }}
                        onClick={() =>
                          exportToCSV(
                            formatNestedDataForCSVexport(
                              getGovernanceColumns(true),
                              getGovernanceData(true),
                            ),
                            `${companyInfo?.Ticker} 
                           Severance / Change in Control ${
                             new Date().toString().split("GMT-")[0]
                           }.csv`,
                          )
                        }
                      >
                        Export Change in Control Governance as CSV
                      </Button>
                    </div>
                  )}
                  <GovernancePeerTable />
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}
    </>
  );
}
